/**
 * OwnMaterial — "Bring your own list" section (landing-v12 P1-②).
 *
 * The persona-2 conversion surface: blog traffic arrives from exam/list/
 * notebook clusters with their OWN material, and their only landing hook
 * used to be a footnote row inside HowItWorks. This section gives the
 * paste/PDF/upload paths a full narrative and a no-install exit ("Open the
 * app" → /login, the hero secondary's exit — persona 2 doesn't need the
 * extension).
 *
 * Honesty gates (landing-v11 §2): the 0–100 worth-learning score runs on
 * the FILE-UPLOAD route only — never promise it for the YouTube tap path;
 * select-all lives on paste/PDF and file uploads, ≤20 cards per confirm;
 * media upload is English-audio-only, 30-min cap. No "one click", no
 * "unlimited".
 */

import { ArrowRight, ClipboardPaste, FileAudio, FileText } from 'lucide-react'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { useLandingT, usePublicHref } from './landing-locale'

const PATHS = [
  { icon: ClipboardPaste, title: 'landing.bring.pasteTitle', desc: 'landing.bring.pasteDesc' },
  { icon: FileText, title: 'landing.bring.pdfTitle', desc: 'landing.bring.pdfDesc' },
  { icon: FileAudio, title: 'landing.bring.fileTitle', desc: 'landing.bring.fileDesc' },
] as const

export function OwnMaterial() {
  const revealRef = useScrollReveal()
  const t = useLandingT()
  const href = usePublicHref()

  return (
    <section id="bring" className="py-16 md:py-24 bg-surface">
      <div ref={revealRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-4 text-balance">
          {t('landing.bring.title')}
        </h2>
        <p className="reveal text-on-surface-variant text-lg text-center mb-12 max-w-2xl mx-auto">
          {t('landing.bring.subtitle')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PATHS.map((path, i) => (
            <div
              key={path.title}
              className={`reveal reveal-stagger-${i + 1} hover-lift rounded-2xl bg-surface-container-low p-6 sm:p-8`}
            >
              <span className="mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-surface-card text-primary">
                <path.icon className="h-5 w-5" strokeWidth={1.5} />
              </span>
              <h3 className="font-headline font-bold text-lg mb-2 text-on-surface">{t(path.title)}</h3>
              <p className="text-sm leading-relaxed text-on-surface-variant">{t(path.desc)}</p>
            </div>
          ))}
        </div>

        <div className="reveal mt-12 flex flex-col items-center gap-3">
          <a
            href={href('/login')}
            className="inline-flex items-center px-7 py-3.5 rounded-full bg-primary text-on-primary font-semibold text-base shadow-card hover:bg-primary-active transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
          >
            {t('landing.bring.cta')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </a>
          <p className="text-muted-soft text-xs">{t('landing.bring.ctaNote')}</p>
        </div>
      </div>
    </section>
  )
}
